import React, { useState, useEffect, useCallback } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, RefreshCw, Pencil, Trash2, Users, AlertCircle, Euro } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { TemplatePickerModal } from '../components/popups/TemplatePickerModal';
import { PopupEditorModal } from '../components/popups/PopupEditorModal';
import { PopupLeadsModal } from '../components/popups/PopupLeadsModal';

const STATUS_LABEL = { active: 'Activo', paused: 'Pausado', draft: 'Borrador' };
const STATUS_COLOR = { active: 'bg-green-500/15 text-green-400', paused: 'bg-yellow-500/15 text-yellow-400', draft: 'bg-gray-500/15 text-gray-400' };

const TRIGGER_LABELS = {
  on_load: 'Al cargar', exit_intent: 'Exit-intent', after_add_to_cart: 'Tras añadir al carrito', inactivity: 'Inactividad',
};

const CampaignCard = ({ campaign, onEdit, onDelete, onToggleStatus, onViewLeads }) => {
  const activeTriggers = Object.entries(campaign.triggers || {})
    .filter(([, v]) => v?.enabled)
    .map(([k]) => TRIGGER_LABELS[k]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5" data-testid={`popup-card-${campaign.campaign_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-white font-medium text-sm truncate">{campaign.name}</h3>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[campaign.status]}`} data-testid={`popup-status-${campaign.campaign_id}`}>
              {STATUS_LABEL[campaign.status]}
            </span>
          </div>
          <p className="text-gray-500 text-xs">
            {activeTriggers.length ? activeTriggers.join(' · ') : 'Sin disparadores activos'}
          </p>
        </div>
        <Switch checked={campaign.status === 'active'} onCheckedChange={() => onToggleStatus(campaign)}
          data-testid={`popup-toggle-active-${campaign.campaign_id}`} />
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-800">
        <button onClick={() => onViewLeads(campaign)} data-testid={`popup-view-leads-${campaign.campaign_id}`}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white text-xs">
          <Users size={13} /> {campaign.lead_count || 0} lead(s)
        </button>
        {campaign.revenue_generated > 0 && (
          <div className="flex items-center gap-1.5 text-green-400 text-xs" data-testid={`popup-revenue-${campaign.campaign_id}`}>
            <Euro size={12} /> {campaign.revenue_generated.toFixed(2)}€ · {campaign.orders_count} pedido(s)
          </div>
        )}
        <div className="flex items-center gap-3">
          <button onClick={() => onEdit(campaign)} data-testid={`popup-edit-${campaign.campaign_id}`}
            className="text-gray-500 hover:text-indigo-400"><Pencil size={14} /></button>
          <button onClick={() => onDelete(campaign)} data-testid={`popup-delete-${campaign.campaign_id}`}
            className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
};

export default function PopupsPage() {
  const { organization } = useOrganization();
  const [campaigns, setCampaigns] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [leadsCampaign, setLeadsCampaign] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/api/popups'),
      api.get('/api/popups/integrations'),
    ]).then(([c, i]) => {
      setCampaigns(c.data.campaigns || []);
      setIntegrations(i.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!organization?.id) return;
    load();
  }, [organization?.id, load]);

  const createFromTemplate = async (templateKey) => {
    try {
      const r = await api.post('/api/popups', { name: 'Nuevo popup', template_key: templateKey });
      setShowPicker(false);
      setCampaigns(prev => [r.data.campaign, ...prev]);
      setEditingCampaign(r.data.campaign);
    } catch (e) {
      toast.error('Error al crear el popup');
    }
  };

  const toggleStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/api/popups/${campaign.campaign_id}`, { status: newStatus });
      setCampaigns(prev => prev.map(c => c.campaign_id === campaign.campaign_id ? { ...c, status: newStatus } : c));
      toast.success(newStatus === 'active' ? 'Popup activado' : 'Popup pausado');
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!window.confirm(`¿Eliminar el popup "${campaign.name}"?`)) return;
    try {
      await api.delete(`/api/popups/${campaign.campaign_id}`);
      setCampaigns(prev => prev.filter(c => c.campaign_id !== campaign.campaign_id));
      toast.success('Popup eliminado');
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="popups-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Popups</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Modales de captación de leads en tu tienda, sincronizados con Klaviyo o Brevo
          </p>
        </div>
        <button
          onClick={() => setShowPicker(true)}
          data-testid="create-popup-btn"
          className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
        >
          <Plus size={15} /> Crear popup
        </button>
      </div>

      <div className="mb-6 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4 text-xs text-gray-400">
        <p className="flex items-center gap-2">
          <AlertCircle size={13} className="text-indigo-400 flex-shrink-0" />
          Los popups se muestran automáticamente en tu tienda una vez tengas al menos uno activo. Revisa que el snippet de instalación esté actualizado en la página "Instalación".
        </p>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[1, 2].map(i => <div key={i} className="h-32 bg-gray-800 rounded-2xl animate-pulse" />)}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 border border-gray-800 rounded-2xl" data-testid="popups-empty-state">
          <p className="text-gray-400 text-sm mb-4">Todavía no has creado ningún popup.</p>
          <button onClick={() => setShowPicker(true)} data-testid="create-popup-empty-btn"
            className="text-indigo-400 hover:text-indigo-300 text-sm font-medium">
            Crea tu primer popup →
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="popups-list">
          {campaigns.map(c => (
            <CampaignCard key={c.campaign_id} campaign={c}
              onEdit={setEditingCampaign} onDelete={deleteCampaign}
              onToggleStatus={toggleStatus} onViewLeads={setLeadsCampaign} />
          ))}
        </div>
      )}

      {showPicker && <TemplatePickerModal onClose={() => setShowPicker(false)} onSelect={createFromTemplate} />}
      {editingCampaign && (
        <PopupEditorModal campaign={editingCampaign} integrations={integrations}
          onClose={() => setEditingCampaign(null)}
          onSaved={() => { setEditingCampaign(null); load(); }} />
      )}
      {leadsCampaign && <PopupLeadsModal campaign={leadsCampaign} onClose={() => setLeadsCampaign(null)} />}
    </div>
  );
}
